import * as types from './actionTypes';
import * as authTypes from '../user/actionTypes';
import AgentClient from '../../focus-client/crud-client/agent-client';
import { collectAgentIds, parseAgentRegistration } from '../../utils/agentUtil';

// How many registration lookups may be in flight at once. Focus has no batch
// endpoint for agents yet (see Phase 1, item 4 of the plan), so a group with
// many trackers would otherwise fire one request per tracker at once.
const MAX_CONCURRENT_LOOKUPS = 8;

const startAgentRegistryFetch = ids => ({
  type: types.AGENT_REGISTRY_FETCH_START,
  payload: ids
});

// entries: { '<id>': registration | null } - null records a resolved-to-nothing
// lookup so it is not retried for the rest of the session.
const mergeAgentRegistrations = entries => ({
  type: types.AGENT_REGISTRY_MERGE,
  payload: { entries }
});

const clearAgentRegistry = () => ({
  type: types.AGENT_REGISTRY_CLEAR
});

// Dispatched directly rather than through UserActions.localLogout(): importing
// user/actions here would close an import cycle, since user/actions triggers
// this slice after the Agents layer lands.
const localLogout = () => ({
  type: authTypes.LOCAL_LOGOUT
});

const normalizeId = value =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase();

// Runs `task` over `items` with at most `limit` in flight at any moment.
const runWithConcurrency = async (items, limit, task) => {
  const queue = items.slice();
  const workers = [];
  const workerCount = Math.max(1, Math.min(limit, queue.length));
  for (let i = 0; i < workerCount; i += 1) {
    workers.push(
      (async () => {
        while (queue.length) {
          const item = queue.shift();
          // eslint-disable-next-line no-await-in-loop
          await task(item);
        }
      })()
    );
  }
  await Promise.all(workers);
};

// Resolves sensor/agent registrations for anything not already known, in flight
// or known to be unresolvable. Safe to call on every refresh and on every
// 20 s LKP tick: with a warm cache it issues no requests at all.
//
// `sources` may be agents facet values, Agents layer features, or both.
const fetchMissingAgentRegistrations = sources => {
  const config = {
    baseURI: document.baseURI
  };

  return async (dispatch, getState) => {
    const registry = getState().agentRegistry || {};
    const known = registry.byId || {};
    const pending = registry.pending || {};
    const failed = registry.failed || {};

    const ids = collectAgentIds(sources).filter(id => {
      const key = normalizeId(id);
      return key && !known[key] && !pending[key] && !failed[key];
    });

    if (!ids.length) return null;

    dispatch(startAgentRegistryFetch(ids));

    const language = getState().ontology.selectedLanguage || '';
    const client = AgentClient(config);
    const entries = {};
    let unauthorised = false;

    await runWithConcurrency(ids, MAX_CONCURRENT_LOOKUPS, async id => {
      let response = null;
      try {
        response = await client.read(id, language);
      } catch (error) {
        response = { isError: true, error };
      }
      if (
        response &&
        response.isError &&
        response.error &&
        response.error.status === 401
      ) {
        unauthorised = true;
        return;
      }
      entries[id] = parseAgentRegistration(response);
    });

    if (unauthorised) {
      dispatch(localLogout());
      return null;
    }

    dispatch(mergeAgentRegistrations(entries));
    return entries;
  };
};

const agentRegistryActions = {
  fetchMissingAgentRegistrations,
  clearAgentRegistry,
  mergeAgentRegistrations,
  startAgentRegistryFetch
};

export default agentRegistryActions;
export {
  fetchMissingAgentRegistrations,
  clearAgentRegistry,
  mergeAgentRegistrations,
  startAgentRegistryFetch,
  runWithConcurrency,
  MAX_CONCURRENT_LOOKUPS
};
